import React, { useState } from 'react';
import { UserPlus, CreditCard, AlertCircle, ArrowUpRight, Zap, ExternalLink } from 'lucide-react';
import { ActivityEvent } from '../../types';

interface DashboardActivityFeedProps {
  events: ActivityEvent[];
  currencySymbol: string;
}

const DashboardActivityFeed: React.FC<DashboardActivityFeedProps> = ({
  events,
  currencySymbol,
}) => {
  const [filter, setFilter] = useState<'all' | 'payments' | 'signups'>('all');

  const filteredEvents = events.filter((ev) => {
    if (filter === 'payments') return ev.type === 'payment_success' || ev.type === 'payment_failed';
    if (filter === 'signups') return ev.type === 'signup';
    return true;
  });

  const getEventMeta = (type: string) => {
    switch (type) {
      case 'signup':
        return {
          icon: UserPlus,
          bg: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400',
          label: 'Customer Onboarded',
        };
      case 'payment_success':
        return {
          icon: CreditCard,
          bg: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950/60 dark:text-indigo-400',
          label: 'Payment Processed',
        };
      case 'payment_failed':
        return {
          icon: AlertCircle,
          bg: 'bg-red-50 text-red-600 dark:bg-red-950/60 dark:text-red-400',
          label: 'Payment Declined',
        };
      case 'upgrade':
        return {
          icon: ArrowUpRight,
          bg: 'bg-amber-50 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400',
          label: 'Plan Upgraded',
        };
      default:
        return {
          icon: Zap,
          bg: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
          label: 'System Activity',
        };
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 p-5 md:p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xs flex flex-col h-full justify-between">
      <div>
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-bold text-slate-900 dark:text-white">Recent Activity</h3>
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping"></span>
          </div>
          <div className="flex bg-slate-100 dark:bg-slate-700/50 p-0.5 rounded-lg text-[10px] font-bold">
            <button
              onClick={() => setFilter('all')}
              className={`px-2 py-1 rounded-md transition-all ${
                filter === 'all'
                  ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs'
                  : 'text-slate-500 hover:text-slate-700 dark:text-slate-400'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('payments')}
              className={`px-2 py-1 rounded-md transition-all ${
                filter === 'payments'
                  ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs'
                  : 'text-slate-500 hover:text-slate-700 dark:text-slate-400'
              }`}
            >
              Payments
            </button>
            <button
              onClick={() => setFilter('signups')}
              className={`px-2 py-1 rounded-md transition-all ${
                filter === 'signups'
                  ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs'
                  : 'text-slate-500 hover:text-slate-700 dark:text-slate-400'
              }`}
            >
              Signups
            </button>
          </div>
        </div>

        <div className="space-y-3.5 overflow-y-auto max-h-[300px] pr-1">
          {filteredEvents.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-xs font-medium">
              No activity matching this filter.
            </div>
          ) : (
            filteredEvents.map((ev) => {
              const meta = getEventMeta(ev.type);
              const Icon = meta.icon;
              return (
                <div
                  key={ev.id}
                  className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors"
                >
                  <div className={`p-2 rounded-lg shrink-0 ${meta.bg}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-slate-900 dark:text-slate-100 truncate">
                      {ev.customerName}
                    </p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      {ev.type === 'payment_success' && (
                        <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                          Paid {currencySymbol}{ev.value}
                        </span>
                      )}
                      {ev.type === 'payment_failed' && (
                        <span className="text-red-500 font-semibold">
                          Failed {currencySymbol}{ev.value} (Retrying)
                        </span>
                      )}
                      {ev.type === 'signup' && 'Created new workspace'}
                      {ev.type === 'upgrade' && 'Upgraded subscription tier'}
                    </p>
                    <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5 font-medium">
                      {new Date(ev.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <button
        onClick={() => {
          if ((window as any).setActiveView) {
            (window as any).setActiveView('reports');
          }
        }}
        className="mt-4 w-full py-2.5 bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 border border-slate-200 dark:border-slate-700"
      >
        <span>View Full Activity Reports</span>
        <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
      </button>
    </div>
  );
};

export default DashboardActivityFeed;
